import { User } from '@/models/user'
import FirebaseService from '@/services/FirebaseService'
import currencyFilter from './currency'
import truncateFilter from './truncate'
import decimal from './decimal'
import displayDateFunc from './displayDate'
import { getDownloadURL, ref } from 'firebase/storage'
import { BudgetItem } from '@/models/budget'
import renderLinksFilter from './renderLinks'
import parseCurrencyValue from './parseCurrencyValue'

declare type LogLevel = 'debug' | 'error' | 'warning' | 'info'

export const logger = (message: string, logLevel: LogLevel, error: Error | {} | null) => {
  console.log(`[${logLevel.toUpperCase()}] - ${message}: ${JSON.stringify(error)}`)
}

export const serializeRoles = (roles: string[]) => {
  if (!roles || !roles.length) return null

  return roles.reduce((map: { [key: string]: boolean }, obj) => {
    map[obj] = true
    return map
  }, {})
}

export const snakeCase = (text: string) => {
  return text.replace(/\W+/g, ' ')
    .split(/ |\B(?=[A-Z])/)
    .map(word => word.toLowerCase())
    .join('_')
}

export const formatSnakeCase = (text: string) => {
  return text.replace(/_/g, ' ')
    .replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c)))
}

export const capitalize = (text: string) => {
  return text.replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())))
}

export const getLocalUser = () => {
  const sessionStorage = window.sessionStorage

  const key = Object.keys(sessionStorage)
    .filter(it => it.startsWith('firebase:authUser'))[0]

  const user = sessionStorage.getItem(key)
  const localUser = user ? JSON.parse(user) : undefined

  if (localUser && localUser.uid) {
    return {
      id: localUser.uid,
      name: localUser.displayName,
      email: localUser.email,
      phone: localUser.phone,
      avatar: '',
      verified: localUser.emailVerified,
      roles: {},
      hubspotId: '',
      address: {},
      billing: {}
    } as User
  }
}

type FileStyleOptions = {
  url: string
  style?: '200x200' | '1080x1080' | '1280x720' | '1920x1080'
  type?: 'webp' | 'jpg'
}
/**
 * Get firebase storage file style from url
 * Styles are store in /styles subfolder
 *
 * @param url
 */
export const getFileStyle = async (options: FileStyleOptions) => {
  return new Promise<string>(async (resolve, reject) => {
    try {
      const storage = await FirebaseService.storage()
      let { url, style, type } = options

      if (!url) return resolve('')
      if (!style) style = '200x200'
      if (!type) type = 'webp'

      const rawImagePath = url.split('/').pop().split('?')[0]
      const fullImagePath = decodeURIComponent(rawImagePath)
      const imagePath = fullImagePath.split('/').slice(0, -1).join('/')
      const imageName = fullImagePath.split('/').pop().split('.')[0]

      const downloadURL = await getDownloadURL(ref(storage, `${imagePath}/styles/${imageName}_${style}.${type}`))
      resolve(downloadURL)
    } catch (error) {
      reject(error)
    }
  })
}

export const fileTypeToString = (file) => {
  const ext = file.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
  return ext ? ext[1].toUpperCase() : 'FILE'
}

export const getFileName = (url: string): string | null => {
  try {
    const normalizedUrl = url.replace(/%2F/g, '/')
    const fileName = normalizedUrl.split('/').pop()?.split('?')[0]
    return fileName || ''
  } catch (_) {
    return ''
  }
}

export const toNumber = (value: string | number) => {
  return parseFloat(String(value).replace(/[^0-9.-]/g, ''))
}

export const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const toFixed = (value, fractionDigits=2) => {
  return toNumber(toNumber(value).toFixed(fractionDigits))
}

export const sortLineItemsByCategoryName = (items: BudgetItem []) => {
  const sortedCategories = [...new Set(items.map(item => item.category))]
    .sort((a, b) => a.weight - b.weight)

  const sortedItems = sortedCategories.reduce((acc, category) => {
    acc[category.name] = items
      .filter(item => item.category.name === category.name)
      .sort((a, b) => a.weight - b.weight)
    return acc
  }, {})
  return sortedItems as { [key: string]: BudgetItem[] }
}

export const isImage = (file: string) => {
  const ext = file.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
  return ext ? ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext[1].toLowerCase()) : false
}

export const getLocalDate = (date: Date) => {
  return new Date(date).toLocaleDateString()
}

export const currency = currencyFilter
export const truncate = truncateFilter
export const displayDate = displayDateFunc
export const decimalFormat = decimal
export const renderLinks = renderLinksFilter
export const parseCurrency = parseCurrencyValue
